import { DataTexture, type NodeMaterial, RGBAFormat, type Texture, UnsignedByteType, Vector2 } from "three/webgpu";
import { uniform } from "three/tsl";
import type { Color } from "../../common/color.js";
import type { Rect } from "../../common/geometry.js";
import type { GpuContext } from "../../engine/GpuContext.js";
import { InstanceRuns } from "../../engine/InstanceRuns.js";
import { configureTexture } from "../../engine/texture.js";
import {
  createQuadMaterial,
  INSTANCE_ATTRIBUTES,
  MODE_GLYPH,
  MODE_GRADIENT,
  MODE_IMAGE,
  MODE_IMAGE_PREMULTIPLIED,
  MODE_RADIAL,
  MODE_SOLID,
  type QuadMaterialUniforms,
} from "./quadMaterial.js";

export {
  MODE_ARC,
  MODE_CAPSULE,
  MODE_CHECKER,
  MODE_DIAMOND,
  MODE_GLOW,
  MODE_GLYPH,
  MODE_GRADIENT,
  MODE_IMAGE,
  MODE_IMAGE_PREMULTIPLIED,
  MODE_POLY,
  MODE_RADIAL,
  MODE_SHINE,
  MODE_SOLID,
  MODE_TRIANGLE,
} from "./quadMaterial.js";

/** 6 x vec4 - rect, color, border color, uv, params, clip. Must match `INSTANCE_ATTRIBUTES`. */
export const FLOATS_PER_INSTANCE = 24;
/** The surface a batch is drawn onto: the canvas, or an off-screen layer. */
export interface SurfaceSize {
  pixelWidth: number;
  pixelHeight: number;
  dpr: number;
  /** Logical size. Defaults to pixel size / dpr. */
  width?: number;
  height?: number;
}

export interface QuadOptions {
  fill?: Color;
  cornerRadius?: number;
  borderWidth?: number;
  borderColor?: Color;
  mode?: number;
  /** u0, v0, u1, v1 in 0..1 texture space. */
  uv?: readonly [number, number, number, number];
  texture?: Texture;
}

const TRANSPARENT_RGBA = { r: 0, g: 0, b: 0, a: 0 } as const;

/**
 * Accumulates one frame's quads and draws them in as few draw calls as the textures allow: a new batch
 * starts only when the sampled texture changes. The clip travels with each quad, so a clip change costs
 * nothing.
 *
 * Only glyph and image quads read their texture, so a quad that names none rides along in whatever batch
 * is open. Without that, every label broke the batch twice - background on white, text on the atlas, next
 * background back on white - and a text-heavy window cost a draw call per string.
 */
export class QuadBatcher {
  #gpu: GpuContext;
  #runs: InstanceRuns;
  #materials = new WeakMap<Texture, NodeMaterial>();
  #uniforms: QuadMaterialUniforms = { viewport: uniform(new Vector2(1, 1)) };
  /** Half the AA band in logical px, cached per frame. */
  #aa = 0.5;
  #dpr = 1;
  #pixelWidth = 1;
  #pixelHeight = 1;

  /**
   * Multiplies the alpha of every quad pushed, fill and border alike.
   *
   * Owned by `RenderContext.pushOpacity`, which is the way to set it: colors reach the shader as straight
   * alpha, so scaling `a` here fades a whole subtree - text, images and gradients included - without any
   * control knowing it is being faded.
   */
  alpha = 1;

  /** 1x1 opaque white, sampled by solid quads that open a batch. */
  readonly white: Texture;

  /** Current clip in logical px, already snapped outward to device pixels: x0, y0, x1, y1. */
  #cx0 = 0;
  #cy0 = 0;
  #cx1 = 0;
  #cy1 = 0;

  constructor(gpu: GpuContext) {
    this.#gpu = gpu;
    const white = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat, UnsignedByteType);
    configureTexture(white, false);
    white.name = "webwin.white";
    white.needsUpdate = true;
    this.white = white;
    this.#runs = new InstanceRuns(gpu, INSTANCE_ATTRIBUTES, (texture) => this.#materialFor(texture));
  }

  get instanceCount(): number {
    return this.#runs.instanceCount;
  }

  get drawCallCount(): number {
    return this.#runs.drawCallCount;
  }

  /** Reset for a new frame. The clip defaults to the whole surface. */
  begin(surface?: SurfaceSize): void {
    this.#runs.reset();
    this.alpha = 1;
    const { pixelWidth, pixelHeight, dpr } = surface ?? this.#gpu.size;
    this.#dpr = dpr;
    this.#aa = 0.5 / dpr;
    this.#pixelWidth = pixelWidth;
    this.#pixelHeight = pixelHeight;
    this.#uniforms.viewport.value.set(surface?.width ?? pixelWidth / dpr, surface?.height ?? pixelHeight / dpr);
    this.setScissor(null);
  }

  /**
   * Set the clip rectangle for subsequent quads. `r` is in logical pixels; it is rounded outward to whole
   * device pixels and clamped to the surface, as a scissor would be.
   */
  setScissor(r: Rect | null): void {
    const dpr = this.#dpr;
    let x0 = 0;
    let y0 = 0;
    let x1 = this.#pixelWidth;
    let y1 = this.#pixelHeight;
    if (r) {
      // Round outward so a clip never shaves a device pixel off its content.
      x0 = Math.max(0, Math.floor(r.x * dpr));
      y0 = Math.max(0, Math.floor(r.y * dpr));
      x1 = Math.max(x0, Math.min(this.#pixelWidth, Math.ceil((r.x + r.width) * dpr)));
      y1 = Math.max(y0, Math.min(this.#pixelHeight, Math.ceil((r.y + r.height) * dpr)));
    }
    this.#cx0 = x0 / dpr;
    this.#cy0 = y0 / dpr;
    this.#cx1 = x1 / dpr;
    this.#cy1 = y1 / dpr;
  }

  /** True when the current clip has zero area, so pushes can be skipped. */
  get clipped(): boolean {
    return this.#cx1 <= this.#cx0 || this.#cy1 <= this.#cy0;
  }

  /** Append one quad. `r` is in logical pixels, already fully transformed. */
  push(r: Rect, opts: QuadOptions): void {
    const mode = opts.mode ?? MODE_SOLID;
    const at = this.#reserve(r.x, r.y, r.width, r.height, opts.texture, mode);
    if (!at) return;
    const d = at.data;
    let i = at.index;

    const fill = opts.fill ?? TRANSPARENT_RGBA;
    const border = opts.borderColor ?? TRANSPARENT_RGBA;
    const borderWidth = opts.borderWidth ?? 0;
    const uv = opts.uv;

    d[i++] = fill.r;
    d[i++] = fill.g;
    d[i++] = fill.b;
    d[i++] = fill.a * this.alpha;

    d[i++] = border.r;
    d[i++] = border.g;
    d[i++] = border.b;
    // In gradient mode the border slot carries the second stop, so its alpha has to survive even though the
    // quad has no border. BOTH gradient modes: leaving MODE_RADIAL out once zeroed every radial ramp's far stop.
    d[i++] = borderWidth > 0 || mode === MODE_GRADIENT || mode === MODE_RADIAL ? border.a * this.alpha : 0;

    d[i++] = uv ? uv[0] : 0;
    d[i++] = uv ? uv[1] : 0;
    d[i++] = uv ? uv[2] : 1;
    d[i++] = uv ? uv[3] : 1;

    d[i++] = opts.cornerRadius ?? 0;
    d[i++] = borderWidth;
    // Half the AA band, in logical px, so the fade spans one device pixel.
    d[i++] = this.#aa;
    d[i++] = mode;
    this.#writeClip(d, i);
  }

  /** Append one glyph quad without an options object. Text is the bulk of every frame, so this allocates nothing. */
  pushGlyph(
    x: number,
    y: number,
    width: number,
    height: number,
    color: Color,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    texture: Texture,
  ): void {
    const at = this.#reserve(x, y, width, height, texture, MODE_GLYPH);
    if (!at) return;
    const d = at.data;
    let i = at.index;
    d[i++] = color.r;
    d[i++] = color.g;
    d[i++] = color.b;
    d[i++] = color.a * this.alpha;
    d[i++] = 0;
    d[i++] = 0;
    d[i++] = 0;
    d[i++] = 0;
    d[i++] = u0;
    d[i++] = v0;
    d[i++] = u1;
    d[i++] = v1;
    d[i++] = 0;
    d[i++] = 0;
    d[i++] = this.#aa;
    d[i++] = MODE_GLYPH;
    this.#writeClip(d, i);
  }

  #writeClip(d: Float32Array, i: number): void {
    d[i++] = this.#cx0;
    d[i++] = this.#cy0;
    d[i++] = this.#cx1;
    d[i] = this.#cy1;
  }

  /**
   * Claim the next instance slot, write its rect and return where the rest goes, or null when the quad is
   * empty or wholly outside the clip.
   */
  #reserve(
    x: number,
    y: number,
    width: number,
    height: number,
    texture: Texture | undefined,
    mode: number,
  ): { data: Float32Array; index: number } | null {
    if (width <= 0 || height <= 0) return null;
    if (this.clipped) return null;
    // The shader pads every quad by one logical px for its AA ring, so the test pads by the same.
    if (x + width + 1 <= this.#cx0 || y + height + 1 <= this.#cy0 || x - 1 >= this.#cx1 || y - 1 >= this.#cy1) {
      return null;
    }

    const open = this.#runs.currentTexture;
    const textured = mode === MODE_GLYPH || mode === MODE_IMAGE || mode === MODE_IMAGE_PREMULTIPLIED;
    const slot = this.#runs.reserve(texture ?? (open && !textured ? open : this.white));
    const d = slot.data;
    const i = slot.index;
    d[i] = x;
    d[i + 1] = y;
    d[i + 2] = width;
    d[i + 3] = height;
    slot.index = i + 4;
    return slot;
  }

  #materialFor(texture: Texture): NodeMaterial {
    let material = this.#materials.get(texture);
    if (!material) {
      material = createQuadMaterial(texture, this.#uniforms);
      this.#materials.set(texture, material);
    }
    return material;
  }

  /** Upload the frame's quads and draw them into whatever the renderer is currently pointed at. */
  flush(): void {
    this.#runs.flush();
  }

  dispose(): void {
    this.#runs.dispose();
    this.white.dispose();
  }
}
